import React from 'react'
import {Link, graphql} from 'gatsby'
import {Location} from '@reach/router'
import {RelatedJourneysFragment} from 'gatsby-graphql-types-generated'
import styled from '@emotion/styled'

import {simpleBorder} from 'src/styles/borders'
import {Header4, Body} from 'src/components/text'
import {Grid, Flex, Box} from 'src/components/Box'
import ButtonLink from 'src/components/ButtonLink'
import SidebarIcon from 'src/components/SidebarIcon'
import useViewer from 'src/hooks/useViewer'
import {localPaths} from 'src/urls'
import useCoreJourneys from 'src/hooks/useCoreJourneys'

export const SidebarContainer = styled.div(({theme}) => ({
  borderRadius: 19,
  ...simpleBorder(),
  backgroundColor: theme.colors.white,
}))

const CircleDash = styled.div<{hideLine: boolean}>(({theme, hideLine}) => ({
  border: `1px solid ${theme.colors.white}`,
  position: 'relative',
  marginRight: theme.space[2],
  marginTop: '-2px',
  '&::after': {
    content: '""',
    width: 2,
    height: hideLine ? 0 : '75%',
    position: 'absolute',
    backgroundColor: theme.colors.grey3,
    top: '24px',
    left: '4px',
    zIndex: 2,
  },
}))

export interface SidebarItem {
  id: string
  name: string
  permalink: string
}

export const SidebarItems = ({items}: {items: SidebarItem[]}) => (
  <>
    {items?.map((item, index) => (
      <Flex mb="2" key={item.name}>
        <CircleDash hideLine={index === items.length - 1}>
          <SidebarIcon iconIndex={index} />
        </CircleDash>
        <Link key={item.id} to={item.permalink}>
          {item.name}
        </Link>
      </Flex>
    ))}
  </>
)

export const SidebarLogin = () => (
  <Grid
    gridAutoFlow="row"
    gridGap="2"
    padding="3"
    borderTop="3px solid"
    borderTopColor="grey2"
  >
    <Header4 fontWeight={500}>
      Let Get Schooled help you achieve your goals
    </Header4>
    <Location>
      {({location}) => (
        <>
          <ButtonLink
            to={localPaths.signUp}
            state={{returnTo: location.pathname}}
            css={{width: '100%', maxWidth: '380px', justifySelf: 'center'}}
          >
            Sign Up
          </ButtonLink>

          <Link
            to={localPaths.login}
            state={{returnTo: location.pathname}}
            css={{justifySelf: 'center'}}
          >
            Log In
          </Link>
        </>
      )}
    </Location>
  </Grid>
)

export const query = graphql`
  fragment RelatedJourneys on ContentfulArticleTile {
    id
    relatedJourneys {
      id
      name
      permalink
    }
  }
`

interface Props {
  relatedJourneys: RelatedJourneysFragment['relatedJourneys']
}

const SidebarContents = ({relatedJourneys}: Props) => {
  const coreJourneys = useCoreJourneys()

  if (relatedJourneys?.length > 0) {
    return (
      <Box p="3">
        <Body color="grey5">Explore your goals</Body>
        <SidebarItems items={relatedJourneys} />
      </Box>
    )
  }

  return (
    <Box p="3">
      <Body color="grey5">Pursue your goals</Body>
      <SidebarItems items={coreJourneys} />
    </Box>
  )
}

const Sidebar = ({relatedJourneys}: Props) => {
  const {loggedIn} = useViewer()
  return (
    <SidebarContainer>
      <SidebarContents relatedJourneys={relatedJourneys} />
      {!loggedIn && <SidebarLogin />}
    </SidebarContainer>
  )
}

export default Sidebar
